import { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { CURRENCIES } from "@/currencyConfig";

export const CurrencySelector = ({ selectedCurrency, onCurrencyChange, isScrolled, isMobile = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);
  const current = CURRENCIES.find((c) => c.code === selectedCurrency) || CURRENCIES[0];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (isMobile) {
    return (
      <div className="border-t border-gray-100 pt-2 mt-1">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="py-3 px-4 text-left text-gray-700 hover:bg-pink-50 hover:text-pink-600 rounded-xl transition-colors flex items-center gap-2 font-semibold w-full"
          data-testid="currency-selector-mobile"
        >
          <span className="text-base">{current.flag}</span>
          <span>{current.symbol} {current.code}</span>
          <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </button>
        {isOpen && (
          <div className="ml-4 border-l-2 border-pink-200 pl-2">
            {CURRENCIES.map((cur) => (
              <button
                key={cur.code}
                onClick={() => { onCurrencyChange(cur.code); setIsOpen(false); }}
                className={`py-2 px-4 text-left rounded-xl transition-colors flex items-center gap-2.5 w-full text-sm ${
                  cur.code === selectedCurrency
                    ? "text-pink-600 bg-pink-50 font-semibold"
                    : "text-gray-600 hover:bg-pink-50 hover:text-pink-600"
                }`}
                data-testid={`currency-option-mobile-${cur.code}`}
              >
                <span className="text-base">{cur.flag}</span>
                <span>{cur.symbol} {cur.code}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-full transition-all text-sm font-semibold ${
          isScrolled
            ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
            : "bg-white/20 text-white hover:bg-white/30"
        }`}
        data-testid="currency-selector"
      >
        <span>{current.symbol} {current.code}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full mt-2 right-0 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden min-w-[150px] animate-fade-in z-50">
          {CURRENCIES.map((cur) => (
            <button
              key={cur.code}
              onClick={() => { onCurrencyChange(cur.code); setIsOpen(false); }}
              className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-colors flex items-center gap-2.5 ${
                cur.code === selectedCurrency
                  ? "text-pink-600 bg-pink-50"
                  : "text-gray-700 hover:bg-gray-50 hover:text-pink-600"
              }`}
              data-testid={`currency-option-${cur.code}`}
            >
              <span className="text-base">{cur.flag}</span>
              <span>{cur.symbol} {cur.code}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CurrencySelector;
